import { useEffect, useCallback, useState } from "react";
import { useUserOrganization } from "./partnerData";
import { Modal, Typography } from "antd";
import AntDesignConfig from "@/antDesignConfig";

type LinkHandlerEvent = MouseEvent | KeyboardEvent;
export type LinkHandler = (event: LinkHandlerEvent, anchor: HTMLAnchorElement) => void;

function getClosestAnchorFromEventTarget(target: EventTarget | null) {
    const el = target instanceof Element ? target : null;
    return (el?.closest("a") as HTMLAnchorElement | null) ?? null;
}

function getFocusedAnchor() {
    const active = document.activeElement;
    if (!active) return null;

    if (active instanceof Element) {
        return (active.closest("a") as HTMLAnchorElement | null) ?? null;
    }
    return null;
}

function isExternalAnchor(anchor: HTMLAnchorElement): boolean {
    const rawHref = anchor.getAttribute("href")?.trim() ?? "";
    if (!rawHref) return false;

    // Skip in-page hash links quickly
    if (rawHref.startsWith("#")) return false;

    // Skip non-http(s) schemes
    // (also avoids treating "mailto:" etc. as external)
    if (/^(mailto|tel|sms|javascript):/i.test(rawHref)) return false;

    let url: URL;
    try {
        // Resolve relative href against current location
        url = new URL(anchor.href, window.location.href);
    } catch {
        return false;
    }

    // Only treat http(s) navigation as external link-outs
    if (url.protocol !== "http:" && url.protocol !== "https:") return false;

    return url.hostname !== window.location.hostname;
}

const useGlobalExternalLinkHandler = () => {
    const { userOrganization } = useUserOrganization();
    const handler: LinkHandler = useCallback((event, anchor) => {
        // Open external links in a new tab
        event.preventDefault();
        setRedirectLink(anchor.href);
    }, []);

    const [redirectLink, setRedirectLink] = useState<string>();
    const navigateToRedirectLink = () => {
        if (redirectLink) {
            window.open(redirectLink, "_blank", "noopener,noreferrer");
            setRedirectLink(undefined);
        }
    };

    const onClick = useCallback(
        (event: MouseEvent) => {
            const anchor = getClosestAnchorFromEventTarget(event.target);
            if (!anchor) return;

            if (!isExternalAnchor(anchor)) return;

            handler(event, anchor);
        },
        [handler]
    );

    const onKeyDown = useCallback(
        (event: KeyboardEvent) => {
            // Activation keys
            if (event.key !== "Enter" && event.key !== " ") return;

            // Optional: ignore modified keys (often indicate other browser behaviors)
            if (event.metaKey || event.ctrlKey || event.altKey) return;

            const anchor = getFocusedAnchor();
            if (!anchor) return;

            if (!isExternalAnchor(anchor)) return;

            handler(event, anchor);
        },
        [handler]
    );

    useEffect(() => {
      if (!userOrganization.isDefault) {
        document.addEventListener("click", onClick, true);
        document.addEventListener("keydown", onKeyDown, true);

        return () => {
            document.removeEventListener("click", onClick, true);
            document.removeEventListener("keydown", onKeyDown, true);
        };
      }
    }, [onClick, onKeyDown, userOrganization.isDefault]);

    return (
        <AntDesignConfig>
            <Modal
                okText="Continue"
                cancelText="Stay"
                open={!!redirectLink}
                onOk={navigateToRedirectLink}
                onCancel={() => setRedirectLink(undefined)}
            >
                <Typography.Title level={3}>
                    You are now leaving the {!userOrganization.isDefault ? userOrganization.shortName : ""} Trial Finder
                    website.
                </Typography.Title>
                <p>
                    The website you are entering is not affiliated with or controlled by us and may have different
                    terms, conditions and privacy and security policies than the{" "}
                    {!userOrganization.isDefault ? userOrganization.shortName : ""} Trial Finder.
                </p>
            </Modal>
        </AntDesignConfig>
    );
};

export default useGlobalExternalLinkHandler;
